// Perceptual sequential color ramps for the deck.gl geo explorer. These geo metrics are NEUTRAL
// (density, accuracy, speed) — NOT health/risk — so we use a viridis-style perceptual ramp rather
// than RAG. Returns RGB tuples deck.gl consumes directly (0..255).

export type Rgb = readonly [number, number, number];

// Viridis control stops (dark blue → teal → green → yellow). Perceptually uniform, colorblind-safe.
const VIRIDIS: readonly Rgb[] = [
  [68, 1, 84],
  [59, 82, 139],
  [33, 145, 140],
  [94, 201, 98],
  [253, 231, 37],
] as const;

// Inferno control stops (black → purple → orange → yellow) — used when higher is "worse" so the
// hot end reads as attention-grabbing without using literal RAG-red semantics.
const INFERNO: readonly Rgb[] = [
  [20, 11, 52],
  [120, 28, 109],
  [221, 81, 58],
  [251, 182, 26],
  [252, 255, 164],
] as const;

const lerp = (a: number, b: number, t: number): number => Math.round(a + (b - a) * t);

const sample = (stops: readonly Rgb[], t: number): Rgb => {
  const clamped = Math.min(1, Math.max(0, t));
  const scaled = clamped * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(scaled));
  const f = scaled - i;
  const a = stops[i];
  const b = stops[i + 1];
  return [lerp(a[0], b[0], f), lerp(a[1], b[1], f), lerp(a[2], b[2], f)];
};

export type RampName = "viridis" | "inferno";

export const rampColor = (ramp: RampName, t: number): Rgb =>
  sample(ramp === "inferno" ? INFERNO : VIRIDIS, t);

// CSS gradient string for a legend swatch (left = low, right = high).
export const rampCssGradient = (ramp: RampName): string => {
  const stops = ramp === "inferno" ? INFERNO : VIRIDIS;
  const parts = stops.map((c, i) => {
    const pct = Math.round((i / (stops.length - 1)) * 100);
    return `rgb(${c[0]}, ${c[1]}, ${c[2]}) ${pct}%`;
  });
  return `linear-gradient(90deg, ${parts.join(", ")})`;
};
